/**
 * Deutsche Feiertage - Daten und Berechnungen
 */

import { addDays, format } from 'date-fns';

export type FederalState =
  | 'BW' | 'BY' | 'BE' | 'BB' | 'HB' | 'HH'
  | 'HE' | 'MV' | 'NI' | 'NW' | 'RP' | 'SL'
  | 'SN' | 'ST' | 'SH' | 'TH';

export const FEDERAL_STATE_NAMES: Record<FederalState, string> = {
  BW: 'Baden-Württemberg',
  BY: 'Bayern',
  BE: 'Berlin',
  BB: 'Brandenburg',
  HB: 'Bremen',
  HH: 'Hamburg',
  HE: 'Hessen',
  MV: 'Mecklenburg-Vorpommern',
  NI: 'Niedersachsen',
  NW: 'Nordrhein-Westfalen',
  RP: 'Rheinland-Pfalz',
  SL: 'Saarland',
  SN: 'Sachsen',
  ST: 'Sachsen-Anhalt',
  SH: 'Schleswig-Holstein',
  TH: 'Thüringen',
};

export interface Holiday {
  date: string; // ISO format YYYY-MM-DD
  name: string;
  nationwide: boolean;
  states?: FederalState[];
}

/**
 * Berechnet Ostersonntag nach der Gaußschen Osterformel
 */
export function calculateEasterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(year, month - 1, day);
}

/**
 * Berechnet Buß- und Bettag (Mittwoch vor dem 23. November)
 */
export function calculateBussUndBettag(year: number): Date {
  const nov23 = new Date(year, 10, 23);
  const dayOfWeek = nov23.getDay();
  
  let daysBack = dayOfWeek - 3; // Mittwoch = 3
  if (daysBack <= 0) {
    daysBack += 7;
  }
  
  return new Date(year, 10, 23 - daysBack);
}

/**
 * Generiert alle Feiertage für ein Jahr
 */
export function getHolidaysForYear(year: number): Holiday[] {
  const easter = calculateEasterSunday(year);
  const bussUndBettag = calculateBussUndBettag(year);
  
  const formatDate = (date: Date): string => format(date, 'yyyy-MM-dd');
  
  const holidays: Holiday[] = [
    // Bundesweite Feiertage (fest)
    {
      date: `${year}-01-01`,
      name: 'Neujahr',
      nationwide: true,
    },
    {
      date: `${year}-05-01`,
      name: 'Tag der Arbeit',
      nationwide: true,
    },
    {
      date: `${year}-10-03`,
      name: 'Tag der Deutschen Einheit',
      nationwide: true,
    },
    {
      date: `${year}-12-25`,
      name: '1. Weihnachtstag',
      nationwide: true,
    },
    {
      date: `${year}-12-26`,
      name: '2. Weihnachtstag',
      nationwide: true,
    },
    
    // Bundesweite Feiertage (beweglich, basierend auf Ostern)
    {
      date: formatDate(addDays(easter, -2)),
      name: 'Karfreitag',
      nationwide: true,
    },
    {
      date: formatDate(addDays(easter, 1)),
      name: 'Ostermontag',
      nationwide: true,
    },
    {
      date: formatDate(addDays(easter, 39)),
      name: 'Christi Himmelfahrt',
      nationwide: true,
    },
    {
      date: formatDate(addDays(easter, 50)),
      name: 'Pfingstmontag',
      nationwide: true,
    },
    
    // Regionale Feiertage (fest)
    {
      date: `${year}-01-06`,
      name: 'Heilige Drei Könige',
      nationwide: false,
      states: ['BW', 'BY', 'ST'],
    },
    {
      date: `${year}-03-08`,
      name: 'Internationaler Frauentag',
      nationwide: false,
      states: ['BE', 'MV'],
    },
    {
      date: `${year}-08-15`,
      name: 'Mariä Himmelfahrt',
      nationwide: false,
      states: ['BY', 'SL'], // BY nur in Gemeinden mit kath. Mehrheit
    },
    {
      date: `${year}-09-20`,
      name: 'Weltkindertag',
      nationwide: false,
      states: ['TH'],
    },
    {
      date: `${year}-10-31`,
      name: 'Reformationstag',
      nationwide: false,
      states: ['BB', 'HB', 'HH', 'MV', 'NI', 'SN', 'ST', 'SH', 'TH'],
    },
    {
      date: `${year}-11-01`,
      name: 'Allerheiligen',
      nationwide: false,
      states: ['BW', 'BY', 'NW', 'RP', 'SL'],
    },
    
    // Regionale Feiertage (beweglich)
    {
      date: formatDate(easter),
      name: 'Ostersonntag',
      nationwide: false,
      states: ['BB'],
    },
    {
      date: formatDate(addDays(easter, 49)),
      name: 'Pfingstsonntag',
      nationwide: false,
      states: ['BB'],
    },
    {
      date: formatDate(addDays(easter, 60)),
      name: 'Fronleichnam',
      nationwide: false,
      states: ['BW', 'BY', 'HE', 'NW', 'RP', 'SL', 'SN', 'TH'], // SN, TH nur in Teilen
    },
    {
      date: formatDate(bussUndBettag),
      name: 'Buß- und Bettag',
      nationwide: false,
      states: ['SN'],
    },
  ];
  
  return holidays.sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Filtert Feiertage für ein bestimmtes Bundesland
 */
export function getHolidaysForState(
  year: number,
  state: FederalState
): Holiday[] {
  const allHolidays = getHolidaysForYear(year);
  
  return allHolidays.filter(
    (holiday) =>
      holiday.nationwide || (holiday.states && holiday.states.includes(state))
  );
}

/**
 * Prüft, ob ein Datum ein Feiertag ist
 */
export function isHoliday(
  date: string,
  year: number,
  state?: FederalState
): Holiday | undefined {
  const holidays = state
    ? getHolidaysForState(year, state)
    : getHolidaysForYear(year).filter((h) => h.nationwide);
  
  return holidays.find((h) => h.date === date);
}

/**
 * Gibt die Anzahl der bundesweiten Feiertage zurück
 */
export function getNationwideHolidayCount(): number {
  return 9; // Neujahr, Karfreitag, Ostermontag, Tag der Arbeit, 
            // Christi Himmelfahrt, Pfingstmontag, Tag der Deutschen Einheit,
            // 1. + 2. Weihnachtstag
}
